/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { JSX } from 'react';
import { NODES_DIMMED_OPACITY } from '@/utils/sankeyChartUtils';
import { SankeyDataNode } from 'types/sankey';

import { RectNode } from './sankey-rect';

interface SankeyLabelProps {
  x: number;
  y: number;
  dy: string;
  textAnchor?: 'start' | 'middle' | 'end';
  text: string;
  node: RectNode;
  onNodeClick?: (node: SankeyDataNode) => void;
  handleNodeClick: (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    node: RectNode
  ) => void;
  nodesWithChildren: Set<string>;
  rootNodeKey: string;
}

const getPercentageFraction = (node: RectNode) => {
  const currentValue = node.value ?? 0;
  const parentValue =
    node.targetLinks?.reduce(
      (acc, cur) => acc + ((cur.source as any)?.value ?? 0),
      0
    ) ?? 0;

  //calculate % of parent value
  const percentage = (currentValue / parentValue) * 100;
  if (percentage % 1 === 0) return percentage.toFixed(0);
  const fixed = percentage.toFixed(2);
  if (fixed.startsWith('0.0') && fixed[3] !== '0') return fixed.slice(0, 4);
  return fixed;
};

const getKFormat = (num: number) => {
  if (!num) return 0;
  if (num <= 999) return num.toString(); //return num as is if less than 1000 (999)
  const k = (num / 1000).toFixed(1);

  if (k.endsWith('.0')) return `${k.slice(0, -2)}k`; //remove .0 if present
  return `${k}k`;
};

const truncateText = (word: string, maxChars = 20) =>
  word.length > maxChars ? `${word.slice(0, maxChars)}…` : word;

export const SankeyLabel = ({
  text,
  node,
  handleNodeClick,
  nodesWithChildren,
  rootNodeKey,
  ...textProps
}: SankeyLabelProps): JSX.Element => {
  const percentageValue = getPercentageFraction(node);
  const LABEL_OPACITY = node.opacity === NODES_DIMMED_OPACITY ? 0.5 : 1;

  const isRootLabel = textProps.textAnchor === 'end'; // label on left is root node
  const isRoot = node.key === rootNodeKey;
  const hasChildren = nodesWithChildren.has(node.key);
  const isExpanded = (node.sourceLinks?.length ?? 0) > 0;

  // split and truncate words on root label, else truncate whole text
  const textLabel =
    isRootLabel && text.includes(' ')
      ? text.split(' ').map((word) => truncateText(word))
      : [truncateText(text)];

  return (
    <g
      onClick={(e: React.MouseEvent<SVGRectElement, MouseEvent>) => {
        // prevent click on disabled nodes
        if (node.isDisabled) return;
        handleNodeClick(e, node);
      }}
      className={node.isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
    >
      <text
        {...textProps}
        fontSize="12"
        fill="currentColor"
        fillOpacity={LABEL_OPACITY}
      >
        {text.length > 20 && <title>{text}</title>}
        {/* Node name on first line */}
        {textLabel.map((word, i) => (
          <tspan
            key={i}
            x={textProps.x}
            dy={i === 0 ? '0' : '1.2em'}
            fontWeight="400"
            fill="#C1C9CF"
            fontSize={'11'}
            letterSpacing={'2%'}
          >
            {word}
          </tspan>
        ))}
        <tspan
          x={textProps.x}
          dy="1.2em"
          fontSize="19"
          fontWeight={'500'}
          fill="#FFFFFF"
        >
          {isNaN(+percentageValue) || percentageValue === 'Infinity' ? (
            <tspan>
              {node.value != undefined
                ? node.value >= 1000
                  ? getKFormat(node.value)
                  : node?.value?.toLocaleString()
                : ''}
            </tspan>
          ) : (
            <>
              {`${percentageValue}% `}
              <tspan fontSize="14" fontWeight={'400'} fill="#C1C9CF">
                (
                {node.value != undefined
                  ? node.value >= 1000
                    ? getKFormat(node.value)
                    : node?.value?.toLocaleString()
                  : ''}
                )
              </tspan>
            </>
          )}
        </tspan>
      </text>
      {/* Chevron indicator */}
      {!isRoot &&
        hasChildren &&
        node.x0 !== undefined &&
        node.x1 !== undefined &&
        node.y0 !== undefined &&
        node.y1 !== undefined && (
          <g
            transform={`translate(${(node.x0 + node.x1) / 2 - -2}, ${(node.y0 + node.y1) / 2 - 7})`} // position chevron
            opacity={LABEL_OPACITY * 0.8}
          >
            <circle
              cx="7"
              cy="7"
              r="7"
              fill="#FFFFFF"
              stroke="#000000"
              strokeWidth="1"
            />
            <path
              d={
                isExpanded
                  ? 'M 9 4 L 5 7 L 9 10' // left chevron
                  : 'M 5 4 L 9 7 L 5 10' // right chevron
              }
              fill="none"
              stroke="#000000"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        )}
    </g>
  );
};

const isFullRectNode = (node: RectNode): node is Required<RectNode> => {
  return (
    node.x0 !== undefined &&
    node.x1 !== undefined &&
    node.y0 !== undefined &&
    node.y1 !== undefined
  );
};

const getTextProps = (node: Required<RectNode>) => {
  // Determine if this is a root/source node (has no incoming links)
  const isRootNode = node.targetLinks.length === 0;

  // Position root nodes' labels on the left, all others on the right
  const position: 'left' | 'right' = isRootNode ? 'left' : 'right';

  // Calculate position based on the determined side
  const x = position === 'right' ? node.x1 + 12 : node.x0 - 12;
  const textAnchor = position === 'right' ? 'start' : 'end';
  const y = (node.y1 + node.y0) / 2;

  return {
    x,
    y,
    textAnchor,
    dy: '0.35em',
  } as const;
};

interface SankeyLabelsProps {
  nodes: RectNode[];
  handleNodeClick: (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    node: RectNode
  ) => void;
  nodesWithChildren: Set<string>;
  rootNodeKey: string;
}

export const SankeyLabels = ({
  nodes,
  handleNodeClick,
  nodesWithChildren,
  rootNodeKey,
}: SankeyLabelsProps): JSX.Element => {
  return (
    <g>
      {nodes.map((node) => {
        if (!isFullRectNode(node)) return null;
        const textProps = getTextProps(node);

        return (
          <SankeyLabel
            {...textProps}
            text={node.name}
            key={node.key}
            node={node}
            handleNodeClick={handleNodeClick}
            nodesWithChildren={nodesWithChildren}
            rootNodeKey={rootNodeKey}
          />
        );
      })}
    </g>
  );
};
